import { api } from "./client";
import type { ApiResponse } from "./types";

// ============================================================================
// The Identity Board's own module.
//
// Every other feature has one; this one did not, so its calls were written
// inline across half a dozen pages and the parts nobody had a page for — the
// counts, the reference vocabularies, the whole nudge lifecycle — were simply
// never called.
//
// Shapes read from docs/api/openapi.json. Three are easy to get wrong:
//
//   · `/reference/{type}` returns a BARE ARRAY.
//   · `/nudges/history` returns a BARE ARRAY.
//   · `/me` is NULLABLE. A member who has not started a board gets null, which
//     is not an error and not an empty object.
// ============================================================================

/** The vocabularies a board is built from. `all` returns every one at once. */
export const REFERENCE_TYPES = [
  "school-types",
  "college-types",
  "sports",
  "music-genres",
  "entertainment",
  "hobbies",
  "spiritual-practices",
  "connection-intents",
  "touchstones",
  "journey-stages",
] as const;

/**
 * Every list sends `code` and `name`: `name` is the word every surface prints
 * for that code. Touchstones also keep `label`. There is no `id`.
 */
export interface ReferenceItem {
  code: string;
  name: string;
  label?: string;
  emoji?: string | null;
  category?: string | null;
  description?: string | null;
  sortOrder?: number;
}

/** BARE ARRAY. `all` returns an object keyed by type instead. */
export function getReferenceData(type: string) {
  return api.get<ApiResponse<ReferenceItem[] | Record<string, ReferenceItem[]>>>(
    `/identity-board/reference/${type}`,
  );
}

export interface MyBoard {
  id: string;
  blocksCompleted: number;
  completenessScore: number;
  /** The gate: discovery does not open until the board earns it. */
  discoveryUnlocked: boolean;
  isDiscoverable: boolean;
  discoverableBy: string;
  matchRadiusMiles: number;
  boardTheme: string | null;
  core: unknown | null;
  journey: unknown | null;
  interests: unknown | null;
  lifestyle: unknown | null;
}

/** NULL when the member has never started a board. Not an error. */
export function getMyBoard() {
  return api.get<ApiResponse<MyBoard | null>>("/identity-board/me");
}

export interface MatchCounts {
  twins: number;
  strong: number;
  moderate: number;
  total: number;
}

/**
 * How many people each tier holds, under the SAME filters the list takes.
 *
 * The point of a separate count endpoint is that it answers before you page
 * through anything — "narrowing to Gujarati leaves you four" is worth knowing
 * before the list reloads, not after.
 */
export function getMatchCounts(params?: {
  originState?: string;
  language?: string;
  connectionIntent?: string;
  minScore?: number;
}) {
  return api.get<ApiResponse<MatchCounts>>(
    "/identity-board/discovery/matches/counts",
    params as Record<string, string | number | boolean | undefined>,
  );
}

export interface WaveCounts {
  /** Waves TO me still waiting on an answer. */
  received: number;
  /**
   * Waves FROM me still OUTSTANDING — not how many I have ever sent.
   *
   * A wave that gets reciprocated becomes `mutual` and stops being counted
   * here, so `sent: 0` next to `mutual: 3` is the ordinary shape of somebody
   * whose waves were all answered. It reads like a bug and is not one.
   */
  sent: number;
  /** Both sides waved. The only one of these that opens a conversation. */
  mutual: number;
}

export function getWaveCounts() {
  return api.get<ApiResponse<WaveCounts>>("/identity-board/discovery/waves/counts");
}

// ── the nudge lifecycle ────────────────────────────────────────────
//
// Four states, and they are four because they mean different things: a nudge
// that was SHOWN and ignored is not the same as one DISMISSED on purpose, and
// neither is one CLICKED but never COMPLETED. Collapsing them would leave the
// completion rate unable to tell "nobody wanted this" from "nobody could
// finish it".

/**
 * A tracking row. Returned by the history list AND by all three outcome calls.
 *
 * The outcomes are BOOLEANS, not timestamps — `clicked: true` says it
 * happened, nothing records when. The spec used to declare `clickedAt`,
 * `dismissedAt` and `completedAt`, so a client reading those got undefined and
 * rendered every nudge as ignored. That is how the drift was found.
 */
export interface NudgeRow {
  id: string;
  nudgeType: string;
  nudgeContext: string | null;
  shownAt: string;
  clicked: boolean;
  dismissed: boolean;
  completedAction: boolean;
}

export interface NudgeStats {
  totalShown: number;
  totalClicked: number;
  totalDismissed: number;
  totalCompleted: number;
  /** Already a rate — do not divide it again. */
  clickRate: number;
  completionRate: number;
}

/** The nudge types the server accepts. Open on the wire, closed here. */
export const NUDGE_TYPES = [
  "onboarding_complete",
  "first_open_reminder",
  "home_incomplete",
  "add_school_block",
  "add_interests_block",
  "weekly_digest",
  "event_context",
] as const;

/**
 * Record that ONE nudge was shown, and get back the id the other three calls
 * need. This has to happen first; nothing can be clicked or dismissed until
 * something has been shown.
 *
 * Takes a TYPE, not an id — the tracking row does not exist until this call
 * creates it. The spec used to document a batch of ids answering with a count,
 * which the handler has never accepted.
 */
export function trackNudgeShown(nudgeType: string, context?: string) {
  return api.post<ApiResponse<{ nudgeId: string }>>("/identity-board/nudges/shown", {
    nudgeType,
    ...(context ? { context } : {}),
  });
}

export function trackNudgeClicked(nudgeId: string) {
  return api.post<ApiResponse<NudgeRow>>(
    `/identity-board/nudges/${nudgeId}/clicked`,
    {},
  );
}

export function trackNudgeDismissed(nudgeId: string) {
  return api.post<ApiResponse<NudgeRow>>(
    `/identity-board/nudges/${nudgeId}/dismissed`,
    {},
  );
}

export function trackNudgeCompleted(nudgeId: string) {
  return api.post<ApiResponse<NudgeRow>>(
    `/identity-board/nudges/${nudgeId}/completed`,
    {},
  );
}

/** BARE ARRAY. */
export function getNudgeHistory() {
  return api.get<ApiResponse<NudgeRow[]>>("/identity-board/nudges/history");
}

/** ADMIN ONLY. An ordinary member gets 403 — expect it and say so. */
export function getNudgeStats() {
  return api.get<ApiResponse<NudgeStats>>("/identity-board/nudges/stats");
}

export interface ActiveNudge {
  id: string;
  nudgeType?: string;
  title?: string;
  message?: string;
  ctaText?: string | null;
  ctaLink?: string | null;
}

/** The nudges currently worth showing. Already called elsewhere; typed here. */
export function getActiveNudges() {
  return api.get<ApiResponse<{ nudges: ActiveNudge[] } | ActiveNudge[]>>(
    "/identity-board/nudges",
  );
}
